import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DemoProvider } from './context/DemoContext';
import { StackFlowProvider } from './context/StackFlowContext';
import { SpinozaProvider } from './context/SpinozaContext';
import SpinozaOverlay from './components/spinoza/SpinozaOverlay';
import { ROLES, getDashboardPath } from './config/roleConfig';

// Layouts
import {
  SuperAdminLayout,
  BankAdminLayout,
  ComplianceMakerLayout,
  ComplianceCheckerLayout,
  TreasuryDealerLayout,
  ForwardsDealerLayout,
  FamilyOfficeLayout,
  IndividualInvestorLayout
} from './components/layouts/DashboardLayouts';

// Auth Pages
import LoginPage from './components/auth/LoginPage';
import MfaChallengePage from './components/auth/MfaChallengePage';
import MfaSetupPage from './components/auth/MfaSetupPage';
import InvestorRegistrationPage from './components/auth/InvestorRegistrationPage';
import BankRegistrationPage from './components/auth/BankRegistrationPage';

// Public Pages
import LandingPage from './components/pages/LandingPage';
import AboutPage from './components/pages/AboutPage';
import ProductsPage from './components/pages/ProductsPage';
import FlowbotPage from './components/pages/FlowbotPage';
import ConfirmSubscriptionPage from './components/pages/ConfirmSubscriptionPage';
import PrivacyPolicyPage from './components/pages/PrivacyPolicyPage';
import DisclaimerPage from './components/pages/DisclaimerPage';
import ReturnsEstimator from './components/pages/ReturnsEstimator';
import InvestorSimulatorPage from './components/simulator/InvestorSimulatorPage';
import BankMarketSimulatorPage from './components/simulator/BankMarketSimulatorPage';
import SimAdminDashboard from './components/simulator/SimAdminDashboard';
import LiveArbitrageExperiment from './components/simulator/live-experiment/LiveArbitrageExperiment';
import ApplyPage from './components/pages/ApplyPage';
import ContactPage from './components/pages/ContactPage';
import PricingPage from './components/pages/PricingPage';
import BookDemoPage from './components/pages/BookDemoPage';
import CookiePolicyPage from './components/pages/CookiePolicyPage';
import DemoAccessPage from './components/pages/DemoAccessPage';

// ==================================================
// DASHBOARD IMPORTS
// ==================================================
import { ForwardsflowAdminDashboard } from './components/dashboards';

// Direct imports for dashboards with full CRUD / URL-path tab reading
import BankAdminDashboard from './components/dashboards/BankAdminDashboard';
import ComplianceMakerDashboard from './components/dashboards/ComplianceMakerDashboard';
import ComplianceCheckerDashboard from './components/dashboards/ComplianceCheckerDashboard';
import TreasuryDealerDashboard from './components/dashboards/TreasuryDealerDashboard';
import ForwardsDealerDashboard from './components/dashboards/ForwardsDealerDashboard';
import FamilyOfficeDashboard from './components/dashboards/FamilyOfficeDashboard';
import IndividualInvestorDashboard from './components/dashboards/IndividualInvestorDashboard';

// KYC Dashboard
import { KycDashboardPage, KycCaseDetailPage } from './pages/Kyc';

// ==================================================
// SUPER ADMIN PAGE COMPONENTS
// ==================================================
import BankManagement from './components/pages/super-admin/BankManagement';
import InvestorManagement from './components/pages/super-admin/InvestorManagement';
import UserManagement from './components/pages/super-admin/UserManagement';
import TransactionOversight from './components/pages/super-admin/TransactionOversight';
import PlatformPL from './components/pages/super-admin/PlatformPL';
import AllInstruments from './components/pages/super-admin/AllInstruments';
import Compliance from './components/pages/super-admin/Compliance';
import RiskManagement from './components/pages/super-admin/RiskManagement';
import AnalyticsDashboard from './components/pages/super-admin/AnalyticsDashboard';
import Notifications from './components/pages/super-admin/Notifications';
import Settings from './components/pages/super-admin/Settings';
import DemoAccessManagement from './components/pages/super-admin/DemoAccessManagement';
import WaitlistManagement from './components/pages/super-admin/WaitlistManagement';
import CSDIntegrationPage from './components/pages/super-admin/CSDIntegrationPage';
import OpenAPIIntegrationPage from './components/pages/super-admin/OpenAPIIntegrationPage';

// Scroll to top on every route change
// Disables browser's automatic scroll restoration which overrides scrollTo
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
};

// Registration Router
const RegisterRouter = () => (
  <div className="min-h-screen bg-gray-50 py-8 px-4">
    <div className="max-w-xl mx-auto text-center">
      <div className="bg-white rounded-2xl shadow-soft p-8 border border-gray-100">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Create Your Account</h1>
        <p className="text-gray-500 mb-8">Choose your account type to get started</p>
        <div className="space-y-4">
          <Link to="/register/investor" className="block w-full p-6 bg-gray-50 rounded-xl border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-all group">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center group-hover:bg-primary-200">
                <svg className="w-6 h-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-gray-900">Impact Investor</h3>
                <p className="text-sm text-gray-500">Access high-yield frontier market opportunities</p>
              </div>
            </div>
          </Link>
          <Link to="/register/bank" className="block w-full p-6 bg-gray-50 rounded-xl border border-gray-200 hover:border-green-300 hover:bg-green-50 transition-all group">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center group-hover:bg-green-200">
                <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-gray-900">Partner Bank</h3>
                <p className="text-sm text-gray-500">Access capital markets and T-bill investment operations</p>
              </div>
            </div>
          </Link>
        </div>
        <p className="mt-6 text-sm text-gray-500">Already have an account? <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">Sign in</Link></p>
      </div>
    </div>
  </div>
);

// Unauthorized Page
const UnauthorizedPage = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
    <div className="text-center">
      <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <svg className="w-10 h-10 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
      <p className="text-gray-500 mb-6">You don't have permission to access this page.</p>
      <a href="/login" className="inline-flex items-center px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700">Return to Login</a>
    </div>
  </div>
);

// MFA Gate — redirects to /mfa/setup if MFA setup is required
const MfaGate = ({ children }) => {
  const { mfaSetupRequired, isAuthenticated } = useAuth();

  if (isAuthenticated && mfaSetupRequired) {
    return <Navigate to="/mfa/setup" replace />;
  }

  return children;
};

// Smart Redirect
const SmartRedirect = () => {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const dashboardPath = getDashboardPath(user.role);
  return <Navigate to={dashboardPath} replace />;
};

function App() {
  return (
    <AuthProvider>
      <DemoProvider>
        <StackFlowProvider>
        <SpinozaProvider>
        <BrowserRouter>
          <ScrollToTop />
          <SpinozaOverlay />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/login/mfa" element={<MfaChallengePage />} />
            <Route path="/mfa/setup" element={<MfaSetupPage />} />
            <Route path="/flowbot" element={<FlowbotPage />} />
            <Route path="/register" element={<RegisterRouter />} />
            <Route path="/register/investor" element={<InvestorRegistrationPage />} />
            <Route path="/register/bank" element={<BankRegistrationPage />} />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/confirm-subscription" element={<ConfirmSubscriptionPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/disclaimer" element={<DisclaimerPage />} />
            <Route path="/returns-estimator" element={<ReturnsEstimator />} />
            <Route path="/simulator" element={<InvestorSimulatorPage />} />
            <Route path="/apply" element={<ApplyPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/demo" element={<BookDemoPage />} />
            <Route path="/demo-access" element={<DemoAccessPage />} />
            <Route path="/cookie-policy" element={<CookiePolicyPage />} />
            <Route path="/terms" element={<DisclaimerPage />} />
            <Route path="/dashboard" element={<SmartRedirect />} />

            {/* ================================================== */}
            {/* FORWARDSFLOW ADMIN (Platform Super Admin) Routes   */}
            {/* Role: forwardsflow_admin                           */}
            {/* ================================================== */}
            <Route path="/admin" element={<MfaGate><SuperAdminLayout /></MfaGate>}>
              <Route index element={<ForwardsflowAdminDashboard />} />
              <Route path="analytics" element={<AnalyticsDashboard />} />
              <Route path="investors" element={<InvestorManagement />} />
              <Route path="banks" element={<BankManagement />} />
              <Route path="users" element={<UserManagement />} />
              <Route path="instruments" element={<AllInstruments />} />
              <Route path="transactions" element={<TransactionOversight />} />
              <Route path="pnl" element={<PlatformPL />} />
              <Route path="compliance" element={<Compliance />} />
              <Route path="risk" element={<RiskManagement />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="settings" element={<Settings />} />
              <Route path="kyc" element={<KycDashboardPage />} />
              <Route path="kyc/cases/:caseId" element={<KycCaseDetailPage />} />
              <Route path="kyc/screening" element={<KycDashboardPage />} />
              <Route path="kyc/reviews" element={<KycDashboardPage />} />
              <Route path="kyc/market-rules" element={<KycDashboardPage />} />
              <Route path="kyc/reporting" element={<KycDashboardPage />} />
              <Route path="demo-access" element={<DemoAccessManagement />} />
              <Route path="waitlist" element={<WaitlistManagement />} />
              <Route path="csd-integration" element={<CSDIntegrationPage />} />
              <Route path="api-integration" element={<OpenAPIIntegrationPage />} />
              <Route path="simulator" element={<SimAdminDashboard />} />
              <Route path="live-simulation" element={<LiveArbitrageExperiment />} />
            </Route>

            {/* ================================================== */}
            {/* BANK ADMIN Routes                                  */}
            {/* Role: bank_admin                                   */}
            {/* ================================================== */}
            <Route path="/bank/admin" element={<MfaGate><BankAdminLayout /></MfaGate>}>
              <Route index element={<BankAdminDashboard />} />
              <Route path="users" element={<BankAdminDashboard />} />
              <Route path="staff" element={<BankAdminDashboard />} />
              <Route path="interfaces" element={<BankAdminDashboard />} />
              <Route path="profile" element={<BankAdminDashboard />} />
              <Route path="audit" element={<BankAdminDashboard />} />
              <Route path="settings" element={<BankAdminDashboard />} />
              <Route path="market-simulator" element={<BankMarketSimulatorPage />} />
              <Route path="kyc" element={<KycDashboardPage />} />
              <Route path="kyc/cases/:caseId" element={<KycCaseDetailPage />} />
            </Route>

            {/* ================================================== */}
            {/* COMPLIANCE MAKER Routes                            */}
            {/* Role: bank_compliance_maker                        */}
            {/* ================================================== */}
            <Route path="/bank/compliance-maker" element={<MfaGate><ComplianceMakerLayout /></MfaGate>}>
              <Route index element={<ComplianceMakerDashboard />} />
              <Route path="kyc" element={<ComplianceMakerDashboard />} />
              <Route path="kyc/cases/:caseId" element={<KycCaseDetailPage />} />
              <Route path="cds" element={<ComplianceMakerDashboard />} />
              <Route path="screening" element={<ComplianceMakerDashboard />} />
              <Route path="documents" element={<ComplianceMakerDashboard />} />
              <Route path="audit" element={<ComplianceMakerDashboard />} />
            </Route>

            {/* ================================================== */}
            {/* COMPLIANCE CHECKER Routes                          */}
            {/* Role: bank_compliance_checker                      */}
            {/* ================================================== */}
            <Route path="/bank/compliance-checker" element={<MfaGate><ComplianceCheckerLayout /></MfaGate>}>
              <Route index element={<ComplianceCheckerDashboard />} />
              <Route path="kyc" element={<ComplianceCheckerDashboard />} />
              <Route path="kyc/cases/:caseId" element={<KycCaseDetailPage />} />
              <Route path="cds" element={<ComplianceCheckerDashboard />} />
              <Route path="screening" element={<ComplianceCheckerDashboard />} />
              <Route path="documents" element={<ComplianceCheckerDashboard />} />
              <Route path="reviews" element={<ComplianceCheckerDashboard />} />
              <Route path="audit" element={<ComplianceCheckerDashboard />} />
            </Route>

            {/* ================================================== */}
            {/* TREASURY DEALER Routes                             */}
            {/* Role: bank_treasury_dealer                         */}
            {/* ================================================== */}
            <Route path="/bank/treasury" element={<MfaGate><TreasuryDealerLayout /></MfaGate>}>
              <Route index element={<TreasuryDealerDashboard />} />
              <Route path="quotes" element={<TreasuryDealerDashboard />} />
              <Route path="settlement" element={<TreasuryDealerDashboard />} />
              <Route path="blotter" element={<TreasuryDealerDashboard />} />
              <Route path="positions" element={<TreasuryDealerDashboard />} />
              <Route path="analytics" element={<TreasuryDealerDashboard />} />
              <Route path="rules" element={<TreasuryDealerDashboard />} />
              <Route path="compliance" element={<TreasuryDealerDashboard />} />
              <Route path="audit" element={<TreasuryDealerDashboard />} />
            </Route>

            {/* ================================================== */}
            {/* FORWARDS DEALER Routes                             */}
            {/* Role: bank_forwards_dealer                         */}
            {/* ================================================== */}
            <Route path="/bank/forwards" element={<MfaGate><ForwardsDealerLayout /></MfaGate>}>
              <Route index element={<ForwardsDealerDashboard />} />
              <Route path="quotes" element={<ForwardsDealerDashboard />} />
              <Route path="settlement" element={<ForwardsDealerDashboard />} />
              <Route path="contracts" element={<ForwardsDealerDashboard />} />
              <Route path="positions" element={<ForwardsDealerDashboard />} />
              <Route path="analytics" element={<ForwardsDealerDashboard />} />
              <Route path="audit" element={<ForwardsDealerDashboard />} />
            </Route>

            {/* ================================================== */}
            {/* FAMILY OFFICE Routes                               */}
            {/* Role: investor_family_office                       */}
            {/* ================================================== */}
            <Route path="/investor/admin" element={<MfaGate><FamilyOfficeLayout /></MfaGate>}>
              <Route index element={<FamilyOfficeDashboard />} />
              {/* Pillar paths */}
              <Route path="yield" element={<FamilyOfficeDashboard />} />
              <Route path="rates" element={<FamilyOfficeDashboard />} />
              <Route path="compliance" element={<FamilyOfficeDashboard />} />
              <Route path="hedging" element={<FamilyOfficeDashboard />} />
              <Route path="liquidity" element={<FamilyOfficeDashboard />} />
              <Route path="execution" element={<FamilyOfficeDashboard />} />
              <Route path="portfolio" element={<FamilyOfficeDashboard />} />
              <Route path="projections" element={<FamilyOfficeDashboard />} />
              <Route path="settlement" element={<FamilyOfficeDashboard />} />
              <Route path="profile" element={<FamilyOfficeDashboard />} />
              <Route path="messages" element={<FamilyOfficeDashboard />} />
              {/* Legacy redirects */}
              <Route path="administration" element={<Navigate to="/investor/admin" replace />} />
              <Route path="markets" element={<Navigate to="/investor/admin/yield" replace />} />
              <Route path="dealing" element={<Navigate to="/investor/admin/execution" replace />} />
              <Route path="fx" element={<Navigate to="/investor/admin/rates" replace />} />
              <Route path="forwards" element={<Navigate to="/investor/admin/hedging" replace />} />
              <Route path="secondary" element={<Navigate to="/investor/admin/liquidity" replace />} />
              <Route path="kyc" element={<KycDashboardPage />} />
              <Route path="kyc/cases/:caseId" element={<KycCaseDetailPage />} />
            </Route>

            {/* ================================================== */}
            {/* INDIVIDUAL INVESTOR Routes                         */}
            {/* Role: investor_individual                          */}
            {/* ================================================== */}
            <Route path="/investor" element={<MfaGate><IndividualInvestorLayout /></MfaGate>}>
              <Route index element={<IndividualInvestorDashboard />} />
              {/* Pillar paths */}
              <Route path="yield" element={<IndividualInvestorDashboard />} />
              <Route path="rates" element={<IndividualInvestorDashboard />} />
              <Route path="compliance" element={<IndividualInvestorDashboard />} />
              <Route path="hedging" element={<IndividualInvestorDashboard />} />
              <Route path="liquidity" element={<IndividualInvestorDashboard />} />
              <Route path="execution" element={<IndividualInvestorDashboard />} />
              <Route path="portfolio" element={<IndividualInvestorDashboard />} />
              <Route path="projections" element={<IndividualInvestorDashboard />} />
              <Route path="settlement" element={<IndividualInvestorDashboard />} />
              <Route path="profile" element={<IndividualInvestorDashboard />} />
              <Route path="messages" element={<IndividualInvestorDashboard />} />
              {/* Legacy redirects */}
              <Route path="settings" element={<Navigate to="/investor" replace />} />
              <Route path="markets" element={<Navigate to="/investor/yield" replace />} />
              <Route path="dealing" element={<Navigate to="/investor/execution" replace />} />
              <Route path="fx" element={<Navigate to="/investor/rates" replace />} />
              <Route path="forwards" element={<Navigate to="/investor/hedging" replace />} />
              <Route path="secondary" element={<Navigate to="/investor/liquidity" replace />} />
            </Route>

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </SpinozaProvider>
        </StackFlowProvider>
      </DemoProvider>
    </AuthProvider>
  );
}

export default App;
